/**
 * DoctorBookingFlow — self-contained interactive booking widget.
 *
 * Used inside DoctorWebsite (Mock B) right rail. Handles:
 *   - 14-day date picker (horizontal scroll)
 *   - Real availability fetched per-location from /api/doctor/{slug}/availability
 *   - Slot selection (status: available / tentative / booked / blocked)
 *   - Booking type (consultation / procedure / surgery)
 *   - Notes
 *   - Login wall when patient not authenticated
 *   - Confirmation success state with "Book another"
 *
 * Accepts optional `controlledDate` and `controlledSlot` props so a parent
 * (the hero teaser card) can pre-select a date / slot from outside.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Calendar, MapPin, AlertCircle, CheckCircle, Loader2, ArrowRight, Smartphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildDays(n = 14) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push(d);
  }
  return out;
}

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export default function DoctorBookingFlow({ doctor, slug, stage, controlledDate, onDateChange }) {
  const navigate = useNavigate();
  const { user, loginWithOtp } = useAuth();
  const [selectedDate, setSelectedDate] = useState(controlledDate || new Date());
  const [availability, setAvailability] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [bookingType, setBookingType] = useState('opd');
  const [bookingNotes, setBookingNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingResult, setBookingResult] = useState(null);
  // Inline passwordless booking (no email signup): mobile + OTP right here.
  const [phoneInput, setPhoneInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [otpFlowUsed, setOtpFlowUsed] = useState(false);
  // Set of ISO dates (YYYY-MM-DD) on which the doctor is on leave inside
  // the visible 14-day window. Used to collapse contiguous leave days
  // into a single compact "9 Jul — 18 Jul · On Leave" pill so the date
  // strip doesn't waste space with N greyed-out buttons.
  const [leaveSet, setLeaveSet] = useState(() => new Set());

  const days = buildDays(14);

  // Fetch leave dates for the visible window (public endpoint — no auth).
  useEffect(() => {
    if (!slug || days.length === 0) return;
    const from = fmtDate(days[0]);
    const to = fmtDate(days[days.length - 1]);
    let cancel = false;
    api.get(`/doctor/${slug}/leaves`, { params: { from_date: from, to_date: to } })
      .then(r => { if (!cancel) setLeaveSet(new Set(r.data?.dates || [])); })
      .catch(() => { if (!cancel) setLeaveSet(new Set()); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Sync controlled date from parent
  useEffect(() => {
    if (controlledDate && fmtDate(controlledDate) !== fmtDate(selectedDate)) {
      setSelectedDate(controlledDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledDate]);

  // Fetch availability when date changes. Also re-fetch when the document
  // becomes visible (patient returns to the tab) and every 60 seconds for
  // "today" — without this, a page left open past a slot's start time
  // keeps showing the now-past slot, which silently fails when the
  // patient finally taps Book. The backend already filters past slots;
  // this just keeps the UI in sync with wall-clock time.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const fetchOnce = (showLoader = true) => {
      if (showLoader) setLoadingSlots(true);
      api.get(`/doctor/${slug}/availability`, { params: { date: fmtDate(selectedDate) } })
        .then(r => { if (!cancelled) setAvailability(Array.isArray(r.data) ? r.data : []); })
        .catch(() => { if (!cancelled) setAvailability([]); })
        .finally(() => { if (!cancelled && showLoader) setLoadingSlots(false); });
    };
    setSelectedSlot(null);
    setSelectedLocation(null);
    fetchOnce(true);

    const today = new Date();
    const isTodaySelected =
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();
    // Auto-refresh only when looking at "today" — other days don't change.
    // Pause the timer whenever the tab is hidden so background tabs cost
    // literally zero server load; visibilitychange below resumes it the
    // moment the patient returns AND fires an immediate catch-up fetch.
    let intervalId = null;
    const startInterval = () => {
      if (!isTodaySelected || intervalId) return;
      intervalId = setInterval(() => fetchOnce(false), 60_000);
    };
    const stopInterval = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    if (!document.hidden) startInterval();
    const onVisible = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        fetchOnce(false);
        startInterval();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [slug, selectedDate]);

  // Client-side past-slot guard. The backend ALREADY filters past slots
  // when looking at today (with a 5-min grace), but if the patient leaves
  // the tab open and time crosses a slot boundary BEFORE the 60-second
  // auto-refresh kicks in, the stale slot would otherwise look bookable.
  // Belt-and-braces: hide any slot whose start time is in the past
  // (5-min grace, same as backend) for the currently-selected "today".
  const visibleAvailability = (() => {
    const today = new Date();
    const isTodaySelected =
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();
    if (!isTodaySelected) return availability;
    const nowMin = today.getHours() * 60 + today.getMinutes() - 5;
    return availability.map(loc => ({
      ...loc,
      slots: (loc.slots || []).filter(s => {
        const [h, m] = (s.start_time || '0:0').split(':').map(Number);
        return h * 60 + m >= nowMin;
      }),
    }));
  })();

  // If the auto-refresh / past-slot guard removes a slot the patient had
  // already selected (because time crossed its start), clear the selection
  // so they don't sit on a Confirm-booking card pointing at a phantom slot.
  useEffect(() => {
    if (!selectedSlot || !selectedLocation) return;
    const loc = visibleAvailability.find(l => l.location_id === selectedLocation.location_id);
    const stillThere = loc?.slots?.some(s => s.start_time === selectedSlot.start_time);
    if (!stillThere) { setSelectedSlot(null); setSelectedLocation(null); }
    // visibleAvailability is recomputed every render, but only changes meaningfully
    // when `availability` or `selectedDate` change — that's what we want to watch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, selectedDate]);

  const pickDate = (d) => {
    setSelectedDate(d);
    onDateChange && onDateChange(d);
  };

  const doBooking = async () => {
    if (!selectedSlot || !selectedLocation) return;
    setBooking(true);
    setBookingError('');
    try {
      const { data } = await api.post('/bookings', {
        doctor_slug: slug,
        location_id: selectedLocation.location_id,
        date: fmtDate(selectedDate),
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        booking_type: bookingType,
        notes: bookingNotes || undefined,
        patient_name: nameInput || undefined,
        patient_phone: phoneInput ? phoneInput.replace(/\D/g, '') : undefined,
      });
      setBookingResult(data);
    } catch (e) {
      setBookingError(formatApiError(e.response?.data?.detail) || 'Could not complete booking');
    } finally { setBooking(false); }
  };

  const handleBook = async () => {
    // Logged-in patient → book directly. Otherwise the inline mobile+OTP
    // mini-form below handles auth (no email-signup redirect).
    if (!user) return;
    if (!selectedSlot || !selectedLocation) return;
    if (!bookingNotes.trim()) { setBookingError('Please enter your symptoms / reason for visit'); return; }
    await doBooking();
  };

  const sendOtp = async () => {
    if (!bookingNotes.trim()) { setOtpError('Please enter your symptoms / reason for visit above'); return; }
    const digits = phoneInput.replace(/\D/g, '');
    if (digits.length < 10) { setOtpError('Enter a valid 10-digit mobile number'); return; }
    setOtpBusy(true); setOtpError(''); setDevOtp('');
    try {
      const { data } = await api.post('/auth/send-otp', { phone: digits });
      setOtpToken(data.otp_token);
      setOtpSent(true);
      if (data._dev_otp) setDevOtp(String(data._dev_otp));
    } catch (e) {
      setOtpError(formatApiError(e.response?.data?.detail) || 'Could not send OTP. Try again.');
    } finally { setOtpBusy(false); }
  };

  const verifyAndBook = async () => {
    if (!otpCode || otpCode.replace(/\D/g, '').length < 4) { setOtpError('Enter the code sent to your phone'); return; }
    setOtpBusy(true); setOtpError('');
    try {
      await api.post('/auth/verify-otp', { otp_token: otpToken, code: otpCode.replace(/\D/g, '') });
      await loginWithOtp({
        phone: phoneInput.replace(/\D/g, ''),
        otp_token: otpToken,
        name: nameInput || null,
        connect_to: slug,
        signup_source: 'booking_otp',
      });
      setOtpFlowUsed(true);
      await doBooking();
    } catch (e) {
      setOtpError(formatApiError(e.response?.data?.detail) || 'Verification failed. Try again.');
    } finally { setOtpBusy(false); }
  };

  // After a passwordless booking, gently send the patient into their own page
  // so they discover it exists (and that mobile+OTP is their key next time).
  useEffect(() => {
    if (bookingResult && otpFlowUsed) {
      const t = setTimeout(() => navigate('/my-clinics'), 4000);
      return () => clearTimeout(t);
    }
  }, [bookingResult, otpFlowUsed, navigate]);

  // ===== Success state =====
  if (bookingResult) {
    return (
      <div className="bg-white rounded-2xl border-2 p-6" style={{ borderColor: stage.accent }} data-testid="booking-success">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: stage.soft }}>
            <CheckCircle size={20} style={{ color: stage.accentDark }} />
          </div>
          <p className="text-base font-semibold text-slate-900">Booked!</p>
        </div>
        <div className="space-y-1.5 text-sm text-slate-700">
          <p><span className="text-slate-500">Date:</span> <b>{selectedDate?.toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}</b></p>
          <p><span className="text-slate-500">Time:</span> <b>{bookingResult.start_time} – {bookingResult.end_time}</b></p>
          <p><span className="text-slate-500">Where:</span> <b>{bookingResult.location_name}</b></p>
        </div>
        <button onClick={() => { setBookingResult(null); setSelectedSlot(null); setBookingNotes(''); }}
          className="mt-5 w-full rounded-xl py-2.5 text-sm font-semibold border border-slate-200 hover:bg-slate-50"
          data-testid="booking-book-another">
          Book another slot
        </button>
        {otpFlowUsed && (
          <div className="mt-4 rounded-xl border p-3" style={{ borderColor: stage.accent, backgroundColor: stage.soft }} data-testid="booking-page-ready">
            <p className="text-xs text-slate-700 leading-relaxed">
              <b>Your patient page is ready.</b> Next time, just log in with your <b>mobile number + OTP</b> — no email or password needed. Opening your page now…
            </p>
            <button onClick={() => navigate('/my-clinics')}
              className="mt-2.5 w-full rounded-xl py-2 text-sm font-bold text-white flex items-center justify-center gap-1.5"
              style={{ backgroundColor: stage.accent }}
              data-testid="booking-view-my-page">
              View my page <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // ===== Main flow =====
  return (
    <div className="space-y-3" data-testid="doctor-booking-flow">
      {/* Date strip */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3 flex items-center gap-1.5" style={{ color: stage.accentDark }}>
          <Calendar size={11} />Select date
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-testid="booking-date-strip">
          {/* Walk `days` and collapse contiguous leave dates into a single
              red pill. Regular dates render as individual buttons; a
              tap on the leave pill jumps the selection to the first
              bookable day AFTER the leave block ends. */}
          {(() => {
            const nodes = [];
            let i = 0;
            const fmtShort = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
            while (i < days.length) {
              const d = days[i];
              const iso = fmtDate(d);
              if (leaveSet.has(iso)) {
                // Find the end of the contiguous leave run.
                let j = i;
                while (j + 1 < days.length && leaveSet.has(fmtDate(days[j + 1]))) j++;
                const leaveStart = days[i];
                const leaveEnd = days[j];
                const nextIdx = j + 1;
                const nextDay = nextIdx < days.length ? days[nextIdx] : null;
                const label = leaveStart === leaveEnd
                  ? `${fmtShort(leaveStart)} · On Leave`
                  : `${fmtShort(leaveStart)} — ${fmtShort(leaveEnd)} · On Leave`;
                nodes.push(
                  <button
                    key={`leave-${fmtDate(leaveStart)}`}
                    type="button"
                    onClick={() => nextDay && pickDate(nextDay)}
                    className="flex-shrink-0 py-2.5 px-3 rounded-xl text-center transition-all border-2 border-rose-300 bg-rose-50 hover:bg-rose-100"
                    title={nextDay ? `Next available: ${nextDay.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}` : 'Doctor on leave'}
                    data-testid={`booking-leave-range-${fmtDate(leaveStart)}-${fmtDate(leaveEnd)}`}
                  >
                    <span className="text-[9px] font-bold uppercase block tracking-wider text-rose-700">On Leave</span>
                    <span className="text-sm font-bold block leading-tight text-rose-700 whitespace-nowrap">{label.replace(' · On Leave', '')}</span>
                    <span className="text-[9px] block text-rose-500">tap for next available</span>
                  </button>
                );
                i = j + 1;
                continue;
              }
              const sel = iso === fmtDate(selectedDate);
              const isToday = iso === fmtDate(new Date());
              nodes.push(
                <button key={iso} onClick={() => pickDate(d)}
                  className={`flex-shrink-0 w-14 py-2.5 rounded-xl text-center transition-all ${sel ? 'shadow-md' : 'border border-slate-200 hover:border-slate-400'}`}
                  style={sel ? { backgroundColor: stage.accent, color: 'white' } : { backgroundColor: 'white', color: 'rgb(71, 85, 105)' }}
                  data-testid={`booking-date-${iso}`}>
                  <span className="text-[9px] font-medium uppercase block tracking-wider">{isToday ? 'Today' : DAY_NAMES[d.getDay() === 0 ? 6 : d.getDay() - 1]}</span>
                  <span className="text-base font-bold block leading-tight">{d.getDate()}</span>
                  <span className="text-[9px] block">{d.toLocaleDateString('en', { month: 'short' })}</span>
                </button>
              );
              i++;
            }
            return nodes;
          })()}
        </div>
      </div>

      {/* Slots */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: stage.accentDark }}>Available times</p>
        <p className="text-xs text-slate-500 mb-3">{selectedDate.toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        {(() => {
          // Detect: looking at today, in IST, after the doctor's last slot.
          // Backend already filters past slots, so empty `slots` on every
          // location AFTER backend filter means "all today's slots are done".
          const today = new Date();
          const isToday =
            selectedDate.getFullYear() === today.getFullYear() &&
            selectedDate.getMonth() === today.getMonth() &&
            selectedDate.getDate() === today.getDate();
          const totalSlotsRemaining = visibleAvailability.reduce((acc, loc) => acc + (loc.slots ? loc.slots.length : 0), 0);

          if (loadingSlots) {
            return <div className="py-6 text-center text-slate-400 flex items-center justify-center gap-1.5"><Loader2 size={14} className="animate-spin" />Loading slots…</div>;
          }
          // Doctor marked this date as leave — replace every location
          // card with a single "Doctor On Leave" banner. Backend already
          // returns a sentinel array with `on_leave: true` on the first
          // element, but we double-check the flag on every element to
          // be resilient.
          if (visibleAvailability.some(loc => loc.on_leave)) {
            return (
              <div className="py-5 text-center rounded-xl bg-rose-50 border border-rose-200" data-testid="booking-doctor-on-leave">
                <p className="text-2xl mb-1.5">🚫</p>
                <p className="text-sm font-semibold text-rose-800">Doctor On Leave</p>
                <p className="text-xs text-rose-700 mt-1.5 px-4 leading-relaxed">The doctor is not available on this day. Pick another date, or use the Message / Call / WhatsApp buttons below for anything urgent.</p>
              </div>
            );
          }
          if (visibleAvailability.length === 0) {
            // No schedules at all for this day.
            if (isToday) {
              return (
                <div className="py-5 text-center rounded-xl bg-amber-50 border border-amber-200" data-testid="booking-holiday-today">
                  <p className="text-2xl mb-1.5">🌴</p>
                  <p className="text-sm font-semibold text-amber-900">It&apos;s a holiday today!</p>
                  <p className="text-xs text-amber-800 mt-1.5 px-4 leading-relaxed">If you still need to connect, use the buttons below — Message, Call, or WhatsApp.</p>
                </div>
              );
            }
            return (
              <div className="py-6 text-center" data-testid="booking-no-slots">
                <AlertCircle size={20} className="text-slate-300 mx-auto mb-1.5" />
                <p className="text-xs text-slate-500">Not available this day. Try another.</p>
              </div>
            );
          }
          if (isToday && totalSlotsRemaining === 0) {
            // Schedules exist but every slot is in the past.
            return (
              <div className="py-5 text-center rounded-xl bg-sky-50 border border-sky-200" data-testid="booking-day-done">
                <p className="text-2xl mb-1.5">🌙</p>
                <p className="text-sm font-semibold text-sky-900">Today&apos;s clinic hours are over.</p>
                <p className="text-xs text-sky-800 mt-1.5 px-4 leading-relaxed">Tap Tomorrow to see fresh slots — or use Message / Call / WhatsApp below for anything urgent.</p>
              </div>
            );
          }
          return (
          <div className="space-y-4">
            {visibleAvailability.filter(loc => loc.slots && loc.slots.length > 0).map(loc => (
              <div key={loc.location_id} data-testid={`booking-loc-${loc.location_id}`}>
                <div className="flex items-start gap-1.5 mb-2">
                  <MapPin size={12} className="mt-0.5 shrink-0" style={{ color: stage.accentDark }} />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{loc.location_name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{loc.location_address} · {loc.schedule_start}–{loc.schedule_end}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {loc.slots.map(slot => {
                    const isSel = selectedSlot?.start_time === slot.start_time && selectedLocation?.location_id === loc.location_id;
                    const st = slot.status || (slot.is_available ? 'available' : 'booked');
                    const canBook = st === 'available' || st === 'tentative';
                    const baseStyle = isSel
                      ? { backgroundColor: stage.accent, color: 'white' }
                      : (st === 'tentative' ? { backgroundColor: '#fef3c7', color: '#92400e' } : { backgroundColor: stage.soft, color: stage.accentDark });
                    return (
                      <button key={`${loc.location_id}-${slot.start_time}`} disabled={!canBook}
                        onClick={() => { setSelectedSlot(slot); setSelectedLocation(loc); }}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                          st === 'blocked' ? 'bg-rose-50 text-rose-300 cursor-not-allowed line-through border border-rose-100' :
                          st === 'booked' ? 'bg-slate-100 text-slate-400 cursor-not-allowed line-through' :
                          'hover:scale-105'
                        }`}
                        style={canBook ? baseStyle : undefined}
                        title={st === 'tentative' ? 'Limited availability' : ''}
                        data-testid={`booking-slot-${loc.location_id}-${slot.start_time}`}>
                        {slot.start_time}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>

      {/* Confirm */}
      {selectedSlot && selectedLocation && (
        <div className="bg-white rounded-2xl border-2 p-4" style={{ borderColor: stage.accent }} data-testid="booking-confirm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: stage.accentDark }}>Confirm booking</p>
          <div className="rounded-xl p-3 mb-3 text-xs space-y-1" style={{ backgroundColor: stage.soft }}>
            <p><span className="text-slate-500">Date:</span> <b className="text-slate-900">{selectedDate.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</b></p>
            <p><span className="text-slate-500">Time:</span> <b className="text-slate-900">{selectedSlot.start_time}–{selectedSlot.end_time}</b></p>
            <p><span className="text-slate-500">Where:</span> <b className="text-slate-900">{selectedLocation.location_name}</b></p>
          </div>
          <div className="space-y-2.5 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Visit type</label>
              <div className="flex gap-1.5">
                {[['opd','Consultation'],['procedure','Procedure'],['surgery','Surgery']].map(([v,l]) => (
                  <button key={v} onClick={() => setBookingType(v)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${bookingType === v ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    style={bookingType === v ? { backgroundColor: stage.accent } : undefined}
                    data-testid={`booking-type-${v}`}>{l}</button>
                ))}
              </div>
              {bookingType === 'surgery' && <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"><AlertCircle size={10} />Requires doctor approval</p>}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Symptoms / reason for visit <span className="text-rose-500">*</span></label>
              <textarea rows={2} maxLength={500} value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)}
                placeholder="Describe your symptoms or reason for visit…"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 outline-none resize-none"
                data-testid="booking-notes" />
            </div>
          </div>
          {bookingError && <p className="text-xs text-rose-600 mb-2 flex items-center gap-1" data-testid="booking-error"><AlertCircle size={12} />{bookingError}</p>}

          {user ? (
            <button onClick={handleBook} disabled={booking}
              className="w-full rounded-xl py-2.5 text-sm font-bold text-white hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-1.5"
              style={{ backgroundColor: stage.accent }}
              data-testid="booking-confirm-btn">
              {booking ? <><Loader2 size={14} className="animate-spin" />Booking…</> : 'Confirm booking'}
            </button>
          ) : (
            <div className="space-y-2.5" data-testid="booking-otp-block">
              <p className="text-[11px] text-slate-500 leading-snug">Confirm with your mobile number — no email or password needed.</p>
              <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5" data-testid="booking-carry-number-note">
                <Smartphone size={13} className="shrink-0 mt-0.5" />
                Carry the <b>same mobile number</b> when you visit the clinic — it ensures your Digital Health Files reach you.
              </p>
              {!otpSent ? (
                <>
                  <input type="text" value={nameInput} onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Your name (optional)"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 outline-none"
                    data-testid="booking-otp-name" />
                  <input type="tel" inputMode="numeric" value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value.replace(/[^0-9]/g, '').slice(0, 13))}
                    placeholder="Enter Patient Mobile No"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 outline-none"
                    data-testid="booking-otp-phone" />
                  {otpError && <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="booking-otp-error"><AlertCircle size={12} />{otpError}</p>}
                  <button onClick={sendOtp} disabled={otpBusy}
                    className="w-full rounded-xl py-2.5 text-sm font-bold text-white hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: stage.accent }}
                    data-testid="booking-send-otp-btn">
                    {otpBusy ? <><Loader2 size={14} className="animate-spin" />Sending…</> : <>Send OTP <ArrowRight size={14} /></>}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-[11px] text-slate-500">Code sent to <b>{phoneInput}</b>. <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpError(''); }} className="text-sky-600 font-semibold" data-testid="booking-otp-change">Change number</button></p>
                  {devOtp && <p className="text-[11px] text-amber-700">Dev OTP: <b>{devOtp}</b></p>}
                  <input type="text" inputMode="numeric" value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    placeholder="Enter OTP"
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm tracking-[0.3em] text-center focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 outline-none"
                    data-testid="booking-otp-code" />
                  {otpError && <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="booking-otp-error"><AlertCircle size={12} />{otpError}</p>}
                  <button onClick={verifyAndBook} disabled={otpBusy || booking}
                    className="w-full rounded-xl py-2.5 text-sm font-bold text-white hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: stage.accent }}
                    data-testid="booking-verify-book-btn">
                    {(otpBusy || booking) ? <><Loader2 size={14} className="animate-spin" />Confirming…</> : 'Verify & confirm booking'}
                  </button>
                  <button onClick={sendOtp} disabled={otpBusy} className="w-full text-xs text-slate-500 hover:text-slate-700 font-medium" data-testid="booking-resend-otp">Resend code</button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
